import { createElement, useId, useRef, useState } from 'react';
import { MatSelectHost } from './MatSelectHost';
import { SelectPanel } from './SelectPanel';

/** The page the user moved to (Angular's PageEvent, without the length it never reads). */
export interface PageEvent {
  pageIndex: number;
  pageSize: number;
}

interface PaginatorProps {
  /** Total number of items (Angular `[length]`). */
  length: number;
  /** Zero-based page (Angular `pageIndex`). */
  pageIndex: number;
  pageSize: number;
  pageSizeOptions: number[];
  onPage: (event: PageEvent) => void;
  className?: string;
  /** Angular `showFirstLastButtons`. */
  showFirstLastButtons?: boolean;
}

/** Material's range label: "1 – 10 of 100" (en dash), and "0 of 0" when empty. */
export function rangeLabel(pageIndex: number, pageSize: number, length: number): string {
  if (length === 0 || pageSize === 0) {
    return `0 of ${length}`;
  }
  const start = pageIndex * pageSize;
  const end = start < length ? Math.min(start + pageSize, length) : start + pageSize;
  return `${start + 1} – ${end} of ${length}`;
}

const NAV_PATHS = {
  first: 'M18.41 16.59L13.82 12l4.59-4.59L17 6l-6 6 6 6zM6 6h2v12H6z',
  previous: 'M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z',
  next: 'M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z',
  last: 'M5.59 7.41L10.18 12l-4.59 4.59L7 18l6-6-6-6zM16 6h2v12h-2z',
} as const;

const NAV_LABELS = { first: 'First page', previous: 'Previous page', next: 'Next page', last: 'Last page' } as const;

function NavButton({
  kind,
  disabled,
  onClick,
}: {
  kind: keyof typeof NAV_PATHS;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`mat-mdc-tooltip-trigger mat-mdc-paginator-navigation-${kind} mdc-icon-button mat-mdc-icon-button mat-unthemed mat-mdc-button-base${disabled ? ' mat-mdc-tooltip-disabled' : ''}`}
      aria-label={NAV_LABELS[kind]}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="mat-mdc-button-persistent-ripple mdc-icon-button__ripple"></span>
      <svg className="mat-mdc-paginator-icon" viewBox="0 0 24 24" focusable="false" aria-hidden="true">
        <path d={NAV_PATHS[kind]} />
      </svg>
      <span className="mat-mdc-focus-indicator"></span>
      <span className="mat-mdc-button-touch-target"></span>
    </button>
  );
}

/** The page-size `<mat-form-field appearance="outline"><mat-select>` of `<mat-paginator>`, same DOM and classes. */
function PageSizeSelect({
  labelId,
  value,
  options,
  onChange,
}: {
  labelId: string;
  value: number;
  options: number[];
  onChange: (size: number) => void;
}) {
  const valueId = useId();
  const triggerRef = useRef<HTMLElement>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = anchor !== null;
  const focused = open;
  return createElement(
    'mat-form-field',
    {
      className: `mat-mdc-form-field mat-mdc-paginator-page-size-select mat-mdc-form-field-type-mat-select mat-form-field-appearance-outline mat-primary${focused ? ' mat-focused' : ''}`,
    },
    <div className="mat-mdc-text-field-wrapper mdc-text-field mdc-text-field--outlined mdc-text-field--no-label">
      <div className="mat-mdc-form-field-flex">
        <div className="mdc-notched-outline mdc-notched-outline--no-label mdc-notched-outline--notched">
          <div className="mdc-notched-outline__leading"></div>
          <div className="mdc-notched-outline__notch"></div>
          <div className="mdc-notched-outline__trailing"></div>
        </div>
        <div className="mat-mdc-form-field-infix">
          <MatSelectHost
            ref={triggerRef}
            className="mat-mdc-select"
            role="combobox"
            tabIndex={0}
            aria-autocomplete="none"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-labelledby={`${valueId} ${labelId}`}
            onClick={() =>
              setAnchor(triggerRef.current?.closest<HTMLElement>('.mat-mdc-text-field-wrapper') ?? triggerRef.current)
            }
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
                event.preventDefault();
                setAnchor(
                  triggerRef.current?.closest<HTMLElement>('.mat-mdc-text-field-wrapper') ?? triggerRef.current,
                );
              }
            }}
          >
            <div className="mat-mdc-select-trigger">
              <div className="mat-mdc-select-value" id={valueId}>
                <span className="mat-mdc-select-value-text">
                  <span className="mat-mdc-select-min-line">{value}</span>
                </span>
              </div>
              <div className="mat-mdc-select-arrow-wrapper">
                <div className="mat-mdc-select-arrow">
                  <svg viewBox="0 0 24 24" width="24px" height="24px" focusable="false" aria-hidden="true">
                    <path d="M7 10l5 5 5-5z" />
                  </svg>
                </div>
              </div>
            </div>
          </MatSelectHost>
        </div>
      </div>
      <div className="mat-mdc-form-field-subscript-wrapper mat-mdc-form-field-bottom-align">
        <div className="mat-mdc-form-field-hint-wrapper">
          <div className="mat-mdc-form-field-hint-spacer"></div>
        </div>
      </div>
      <SelectPanel
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        labelledBy={labelId}
        options={options.map((option) => ({ key: String(option), label: String(option), selected: option === value }))}
        onSelect={(option) => {
          setAnchor(null);
          if (Number(option.key) !== value) onChange(Number(option.key));
        }}
      />
    </div>,
  );
}

/**
 * Equivalent of `<mat-paginator>`: same DOM, classes, labels, page-size choices and page event (Material's own
 * stylesheet is vendored, so it renders exactly like Angular's).
 */
export function Paginator({
  length,
  pageIndex,
  pageSize,
  pageSizeOptions,
  onPage,
  className,
  showFirstLastButtons = false,
}: PaginatorProps) {
  const labelId = useId();
  const lastPage = Math.max(Math.ceil(length / pageSize) - 1, 0);
  const canPrevious = pageIndex >= 1 && pageSize !== 0;
  const canNext = pageIndex < Math.ceil(length / pageSize) - 1 && pageSize !== 0;
  const options = pageSizeOptions.includes(pageSize)
    ? pageSizeOptions
    : [...pageSizeOptions, pageSize].sort((a, b) => a - b);

  const changePageSize = (next: number) => {
    // Material keeps the first visible item in view when the page size changes.
    const firstItem = pageIndex * pageSize;
    onPage({ pageIndex: Math.floor(firstItem / next), pageSize: next });
  };

  return createElement(
    'mat-paginator',
    { role: 'group', className: `mat-mdc-paginator ${className ?? ''}`.trim() },
    <div className="mat-mdc-paginator-outer-container">
      <div className="mat-mdc-paginator-container">
        <div className="mat-mdc-paginator-page-size">
          {options.length > 1 && (
            <>
              <div className="mat-mdc-paginator-page-size-label" id={labelId}>
                Items per page:
              </div>
              <PageSizeSelect labelId={labelId} value={pageSize} options={options} onChange={changePageSize} />
            </>
          )}
          {options.length <= 1 && <div className="mat-mdc-paginator-page-size-value">{pageSize}</div>}
        </div>
        <div className="mat-mdc-paginator-range-actions">
          <div className="mat-mdc-paginator-range-label" aria-live="polite">
            {rangeLabel(pageIndex, pageSize, length)}
          </div>
          {showFirstLastButtons && (
            <NavButton kind="first" disabled={!canPrevious} onClick={() => onPage({ pageIndex: 0, pageSize })} />
          )}
          <NavButton
            kind="previous"
            disabled={!canPrevious}
            onClick={() => onPage({ pageIndex: pageIndex - 1, pageSize })}
          />
          <NavButton kind="next" disabled={!canNext} onClick={() => onPage({ pageIndex: pageIndex + 1, pageSize })} />
          {showFirstLastButtons && (
            <NavButton kind="last" disabled={!canNext} onClick={() => onPage({ pageIndex: lastPage, pageSize })} />
          )}
        </div>
      </div>
    </div>,
  );
}
